import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Location } from '../../../assests/ExportImageIcon'
import { showToast } from '../../../componants/toast/toastService'
import service from '../../../services/service'
import endpoints from '../../../services/endpoints'
import '../venueShared.css'

const formatMoney = (value, currency) => {
    if (value === undefined || value === null || value === '') return null
    const amount = Number(value)
    if (Number.isNaN(amount) || amount === 0) return null
    const symbol = !currency || currency === 'INR' ? '₹' : `${currency} `
    return `${symbol}${amount.toLocaleString('en-IN')}`
}

const VenueConfirmBooking = () => {
    const navigate = useNavigate()
    const { id } = useParams()
    const user = useSelector((state) => state.auth?.user)

    const [details, setDetails] = useState(null)
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState(false)
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [address, setAddress] = useState('')
    const [paying, setPaying] = useState(false)

    const [availableCoupons, setAvailableCoupons] = useState([])
    const [couponCode, setCouponCode] = useState('')
    const [couponBusy, setCouponBusy] = useState(false)
    const [walletAmount, setWalletAmount] = useState('')
    const [walletBusy, setWalletBusy] = useState(false)

    const fetchDetails = () => {
        if (!id) return
        setLoading(true)
        setLoadError(false)
        service
            .get(endpoints.venues.proposalConfirmBookingDetails.replace('{proposal_id}', id), {}, { silent: true })
            .then(({ data }) => {
                const body = data?.data ?? data ?? null
                if (!body) throw new Error('empty response')
                setDetails(body)
            })
            .catch(() => {
                setDetails(null)
                setLoadError(true)
            })
            .finally(() => setLoading(false))
    }

    useEffect(fetchDetails, [id])

    useEffect(() => {
        if (user) {
            setFirstName(user.first_name || user.firstName || '')
            setLastName(user.last_name || user.lastName || '')
            setEmail(user.email || '')
            setPhone(user.phone || user.mobile || '')
            setAddress(user.address || '')
        }
    }, [user])

    useEffect(() => {
        service
            .get(endpoints.bookings.coupons, { service: 'venue', limit: 12 }, { silent: true })
            .then(({ data }) => setAvailableCoupons(data?.data?.coupons || []))
            .catch(() => setAvailableCoupons([]))
    }, [])

    // Confirmed live 2026-09-21: apply-coupon's response already carries the
    // updated {fare_breakup, payment_schedule, first_installment_amount} —
    // merge it straight into `details` instead of doing a whole extra
    // fetchDetails() round-trip. It doesn't echo back `coupon_code` itself,
    // so that's set locally from what was just submitted.
    const handleApplyCoupon = (code) => {
        const codeToApply = code || couponCode
        if (!codeToApply) {
            showToast('Please enter a coupon code', 'error')
            return
        }
        if (couponBusy) return
        setCouponBusy(true)
        service
            .post(endpoints.venues.applyCoupon.replace('{proposal_id}', id), { code: codeToApply })
            .then(({ data }) => {
                const body = data?.data ?? data ?? {}
                setCouponCode(codeToApply)
                setDetails((prev) => (prev ? { ...prev, ...body, coupon_code: codeToApply } : prev))
            })
            .catch(() => {
                // error toast already shown by the service interceptor
            })
            .finally(() => setCouponBusy(false))
    }

    const handleRemoveCoupon = () => {
        if (couponBusy) return
        setCouponBusy(true)
        service
            .post(endpoints.venues.removeCoupon.replace('{proposal_id}', id), {})
            .then(({ data }) => {
                const body = data?.data ?? data ?? {}
                setCouponCode('')
                setDetails((prev) => (prev ? { ...prev, ...body, coupon_code: '' } : prev))
            })
            .catch(() => {
                // error toast already shown by the service interceptor
            })
            .finally(() => setCouponBusy(false))
    }

    const handleToggleWallet = () => {
        if (walletBusy) return
        const isApplied = !!details?.wallet_applied
        if (!isApplied && !walletAmount) {
            showToast('Please enter an amount to apply from your wallet', 'error')
            return
        }
        setWalletBusy(true)
        const action = isApplied
            ? service.post(endpoints.venues.removeWallet.replace('{proposal_id}', id), {})
            : service.post(endpoints.venues.applyWallet.replace('{proposal_id}', id), { amount: Number(walletAmount) })
        action
            .then(() => {
                setWalletAmount('')
                fetchDetails()
            })
            .catch(() => {
                // error toast already shown by the service interceptor
            })
            .finally(() => setWalletBusy(false))
    }

    const handlePay = () => {
        if (!firstName || !lastName) {
            showToast('Please enter your full name', 'error')
            return
        }
        if (!email || !phone) {
            showToast('Please enter your email and mobile number', 'error')
            return
        }
        if (!address) {
            showToast('Please enter your address', 'error')
            return
        }
        if (paying) return

        setPaying(true)
        // Confirmed via Swagger 2026-09-21: Pay now calls `action` (accept),
        // not `book` — the accept action itself carries the fare breakdown,
        // schedule, and contact details in one go. `quotation_id` doubles as
        // both the path param and a body field per the same schema.
        const fareBreakupSource = details?.fare_breakup || {}
        const paymentScheduleRaw = Array.isArray(details?.payment_schedule) ? details.payment_schedule : []
        service
            .post(endpoints.venues.proposalAction.replace('{proposal_id}', id), {
                action: 'accept',
                reason: '',
                quotation_id: Number(id),
                fare_breakup: {
                    subtotal: fareBreakupSource.subtotal,
                    taxes_and_fees: fareBreakupSource.taxes_and_fees,
                    platform_fee: fareBreakupSource.platform_fee,
                    discount: fareBreakupSource.discount,
                    // Schema lists both — GET confirm-booking-details only
                    // ever returns `payable_amount`, so mirror it into
                    // `total_amount` too rather than leaving it blank.
                    total_amount: fareBreakupSource.payable_amount,
                    payable_amount: fareBreakupSource.payable_amount,
                    currency: fareBreakupSource.currency,
                },
                payment_schedule: paymentScheduleRaw.map((item) => ({
                    step: item.step,
                    title: item.title,
                    amount: item.amount,
                    currency: item.currency,
                    due_text: item.due_text,
                })),
                first_name: firstName,
                last_name: lastName,
                email,
                phone,
                address,
            })
            .then(({ data }) => {
                // Confirmed live 2026-09-21: accepting now returns the Stripe
                // PaymentIntent directly — {booking_id, chat_room_id,
                // quotation_id, client_secret, payment_intent_id,
                // publishable_key, gateway: "stripe", amount, currency}.
                // Carried forward so venuePayment.jsx can confirm the card
                // payment without a separate checkout call.
                const body = data?.data ?? data ?? {}
                navigate(`/venue-payment/${id}`, {
                    state: {
                        bookingId: body.booking_id ?? null,
                        clientSecret: body.client_secret || '',
                        paymentIntentId: body.payment_intent_id || '',
                        publishableKey: body.publishable_key || '',
                        gateway: body.gateway || 'stripe',
                        payAmount: body.amount ?? null,
                        payCurrency: body.currency || fareBreakupSource.currency || 'INR',
                    },
                })
            })
            .catch(() => {
                // error toast already shown by the service interceptor
            })
            .finally(() => setPaying(false))
    }

    if (loading) {
        return (
            <section className='commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <p className='hotelResultsEmpty'>Loading booking details...</p>
                </div>
            </section>
        )
    }

    if (loadError || !details) {
        return (
            <section className='commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <p className='hotelResultsEmpty'>We couldn&apos;t load your booking details. Please go back and try again.</p>
                    <button type='button' className='commonBtn' onClick={() => navigate('/venue-booking-confirm')}>Back to Proposals</button>
                </div>
            </section>
        )
    }

    const venueDetails = details.venue_details || {}
    const venueBooking = {
        name: venueDetails.name || '',
        location: venueDetails.location || '',
    }

    const bookingSummary = [
        { label: 'Event Type:', value: venueDetails.event_type || '—' },
        { label: 'Date:', value: venueDetails.date || '—' },
        { label: 'Time Slot:', value: venueDetails.time_slot || '—' },
        { label: 'Guests:', value: String(venueDetails.guests ?? '—') },
    ]

    const paymentSchedule = Array.isArray(details.payment_schedule)
        ? details.payment_schedule.map((item, index) => ({
            step: item.step ?? index + 1,
            title: item.title || '',
            due: item.due_text || '',
            value: formatMoney(item.amount, item.currency) || '—',
        }))
        : []

    const terms = Array.isArray(details.terms_and_conditions) ? details.terms_and_conditions : []

    const fareBreakupSource = details.fare_breakup || {}
    const fareBreakup = [
        { label: 'Subtotal', value: formatMoney(fareBreakupSource.subtotal, fareBreakupSource.currency) },
        { label: 'Taxes & Fees', value: formatMoney(fareBreakupSource.taxes_and_fees, fareBreakupSource.currency) },
        { label: 'Platform Fee', value: formatMoney(fareBreakupSource.platform_fee, fareBreakupSource.currency) },
    ].filter((item) => item.value)

    // Confirmed live 2026-09-21: the field is `payable_amount`, not
    // `total_amount` — this was always falling back to "—". `discount` is
    // also nested inside fare_breakup, not a top-level `discount_amount`.
    const totalAmount = formatMoney(fareBreakupSource.payable_amount, fareBreakupSource.currency) || '—'
    const discountAmount = formatMoney(fareBreakupSource.discount, fareBreakupSource.currency)
    const walletApplied = formatMoney(details.wallet_applied)
    // `first_installment_amount` (confirmed live 2026-09-21) — not
    // payment_schedule[0], per Jatin.
    const payNowLabel = formatMoney(details.first_installment_amount, fareBreakupSource.currency) || totalAmount

    return (
        <section className='commonTopPadding commonBottomPadding'>
            <div className='container'>
                <div className='row g-4'>
                    <div className='col-md-8'>
                        <div className='bsBox'>
                            <div className='hotelCard__name'>{venueBooking.name}</div>
                            <div className='hotelCard__location mt-2'>
                                <img src={Location} alt="" />
                                <div className='hotelCard__locationName'>{venueBooking.location}</div>
                            </div>

                            <div className='venueInfoRow mt-3'>
                                {bookingSummary.map((item) => (
                                    <div className='bsFareRow' key={item.label}>
                                        <span>{item.label}</span>
                                        <span>{item.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className='bsBox mt-3'>
                            <div className='bsSectionTitle mb-3'>Contact Details</div>
                            <div className='travellerFieldRow'>
                                <div className='travellerField'>
                                    <label>First/Middle Name</label>
                                    <input type='text' value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                                </div>
                                <div className='travellerField'>
                                    <label>Last Name</label>
                                    <input type='text' value={lastName} onChange={(e) => setLastName(e.target.value)} />
                                </div>
                            </div>
                            <div className='travellerFieldRow'>
                                <div className='travellerField'>
                                    <label>Email Address</label>
                                    <input type='email' value={email} onChange={(e) => setEmail(e.target.value)} />
                                </div>
                                <div className='travellerField'>
                                    <label>Mobile Number</label>
                                    <input type='tel' value={phone} onChange={(e) => setPhone(e.target.value)} />
                                </div>
                            </div>
                            <div className='travellerFieldRow'>
                                <div className='travellerField'>
                                    <label>Address</label>
                                    <input type='text' value={address} onChange={(e) => setAddress(e.target.value)} />
                                </div>
                            </div>
                            <p className='bsFareNote'>Booking confirmation will be sent here</p>
                        </div>

                        <button type='button' className='commonBtn w-100 mt-3' disabled={paying} onClick={handlePay}>
                            {paying ? 'Please wait...' : `Pay ${payNowLabel}`}
                        </button>
                        <p className='bsFareNote totalPayNote text-center'>By proceeding, you agree to our Terms &amp; Conditions</p>
                    </div>

                    <div className='col-md-4'>
                        <div className='bookingSidebar'>
                            <div className='bsSection'>
                                <div className='bsSectionTitle'>Payment Schedule</div>
                                <div className='bsBox'>
                                    {paymentSchedule.map((item) => (
                                        <div className='venueScheduleRow' key={item.step}>
                                            <div className='venueScheduleRow__label'>
                                                <span className='venueScheduleRow__step'>{item.step}</span>
                                                <div>
                                                    <div className='venueScheduleRow__title'>{item.title}</div>
                                                    <div className='venueScheduleRow__due'>{item.due}</div>
                                                </div>
                                            </div>
                                            <span className='venueScheduleRow__value'>{item.value}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className='bsSection'>
                                <div className='bsSectionTitle'>Terms &amp; Conditions</div>
                                <div className='bsBox'>
                                    <ul className='bsInfoList'>
                                        {terms.map((term, index) => (
                                            <li key={index}>{term}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            <div className='bsSection'>
                                <div className='bsSectionTitle'>Fare Breakup</div>
                                <div className='bsBox'>
                                    {fareBreakup.map((item) => (
                                        <div className='bsFareRow' key={item.label}>
                                            <span>{item.label}</span>
                                            <span>{item.value}</span>
                                        </div>
                                    ))}
                                    {discountAmount ? (
                                        <div className='bsFareRow bookingDiscountRow'>
                                            <span>Discount Applied</span>
                                            <span>- {discountAmount}</span>
                                        </div>
                                    ) : null}
                                    {walletApplied ? (
                                        <div className='bsFareRow bookingDiscountRow'>
                                            <span>Wallet Applied</span>
                                            <span>- {walletApplied}</span>
                                        </div>
                                    ) : null}
                                    <div className='bsFareDivider' />
                                    <div className='bsFareRow bsFareRow--total'>
                                        <span>TOTAL AMOUNT</span>
                                        <span>{totalAmount}</span>
                                    </div>
                                    <p className='bsFareNote mt-2 mb-0'>Coupon discount applies only to the first advance payment.</p>
                                </div>
                            </div>

                            {(details.coupon_code || availableCoupons.length > 0) && (
                            <div className='bsSection'>
                                <div className='bsSectionTitle'>Offers &amp; Discounts</div>
                                <div className='bsBox'>
                                    {details.coupon_code ? (
                                        <div className='bsCouponCard'>
                                            <div className='bsCouponHead'>
                                                <span className='bsCouponCode'>{details.coupon_code}</span>
                                                {discountAmount ? <span className='bsCouponOff'>{discountAmount} off</span> : null}
                                            </div>
                                            <button type='button' className='bsCouponApply' disabled={couponBusy} onClick={handleRemoveCoupon}>
                                                {couponBusy ? 'Removing...' : 'REMOVE COUPON'}
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <input
                                                type='text'
                                                className='bsCouponInput'
                                                placeholder='Enter coupon code'
                                                value={couponCode}
                                                onChange={(e) => setCouponCode(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon()}
                                            />
                                            {availableCoupons.map((coupon, index) => (
                                                <div className='bsCouponCard' key={coupon.code || index}>
                                                    <div className='bsCouponHead'>
                                                        <span className='bsCouponCode'>{coupon.code}</span>
                                                        <span className='bsCouponOff'>{coupon.discount_label || coupon.off}</span>
                                                    </div>
                                                    <p className='bsCouponDesc'>{coupon.description || coupon.desc}</p>
                                                    <button
                                                        type='button'
                                                        className='bsCouponApply'
                                                        disabled={couponBusy}
                                                        onClick={() => handleApplyCoupon(coupon.code)}
                                                    >
                                                        {couponBusy ? 'Applying...' : 'APPLY COUPON'}
                                                    </button>
                                                </div>
                                            ))}
                                        </>
                                    )}
                                </div>
                            </div>
                            )}

                            {/* No wallet-balance API integrated yet — can't honestly condition this
                                section on "user has a wallet balance" without that data. Re-enable
                                once a real balance field/endpoint is confirmed.
                            <div className='bsSection'>
                                <div className='bsSectionTitle'>Wallet</div>
                                <div className='bsBox'>
                                    {details.wallet_applied ? (
                                        <div className='bsFareRow'>
                                            <span>Wallet balance applied</span>
                                            <button type='button' className='bsCouponApply' disabled={walletBusy} onClick={handleToggleWallet}>
                                                {walletBusy ? 'Please wait...' : 'REMOVE'}
                                            </button>
                                        </div>
                                    ) : (
                                        <div className='bsFareRow'>
                                            <input
                                                type='number'
                                                min={0}
                                                className='bsCouponInput'
                                                placeholder='Amount to apply'
                                                value={walletAmount}
                                                onChange={(e) => setWalletAmount(e.target.value)}
                                            />
                                            <button type='button' className='bsCouponApply' disabled={walletBusy} onClick={handleToggleWallet}>
                                                {walletBusy ? 'Please wait...' : 'APPLY'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            */}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default VenueConfirmBooking
